import { useState, useEffect } from "react";
import { Lock, Eye, EyeOff, ArrowLeft, Key } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import AuthLayout from "../../components/auth/AuthLayout";
import AuthInput from "../../components/auth/AuthInput";
import AuthButton from "../../components/auth/AuthButton";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  resetPassword,
  clearError,
  clearResetPasswordSuccess,
  selectAuthLoading,
  selectAuthError,
  selectResetPasswordSuccess,
} from "../../store/slices/authSlice";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const [email] = useState(searchParams.get("email") || "");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectAuthLoading);
  const error = useAppSelector(selectAuthError);
  const resetPasswordSuccess = useAppSelector(selectResetPasswordSuccess);

  const handleResetPassword = async () => {
    if (!email || !otp || !newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }

    const result = await dispatch(
      resetPassword({
        email,
        otp,
        password: newPassword,
        password_confirmation: confirmPassword,
      })
    );
    if (resetPassword.fulfilled.match(result)) {
      toast.success("Password reset successfully!");
      navigate("/signin", { replace: true });
    }
  };

  // Handle error states
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Handle success states
  useEffect(() => {
    if (resetPasswordSuccess) {
      dispatch(clearResetPasswordSuccess());
    }
  }, [resetPasswordSuccess, dispatch]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleResetPassword();
    }
  };

  return (
    <AuthLayout
      title="Reset Password"
      subtitle="Enter OTP and your new password"
    >
      {/* OTP Input */}
      <AuthInput
        icon={Key}
        type="text"
        placeholder="Enter OTP"
        value={otp}
        onChange={(e) => setOtp(e.target.value)}
        onKeyPress={handleKeyPress}
        maxLength={6}
      />

      {/* New Password Input */}
      <div className="relative group">
        <div className="relative rounded-xl p-[1px] bg-gradient-to-br from-emerald-400/30 via-transparent to-emerald-400/30 transition-all duration-300 group-hover:from-emerald-400/50 group-hover:to-emerald-400/50 group-focus-within:from-emerald-500/60 group-focus-within:to-emerald-500/60">
          <div className="flex items-center gap-3 rounded-[0.95rem] bg-white/95 px-4 py-3 ring-1 ring-gray-200/50 shadow-sm backdrop-blur-md transition-all duration-300 group-hover:ring-emerald-300/60 group-hover:shadow-md group-focus-within:ring-emerald-400/80 group-focus-within:shadow-lg group-focus-within:bg-white">
            <Lock
              size={18}
              className="text-emerald-600 transition-colors duration-300 group-focus-within:text-emerald-700"
            />
            <input
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New Password"
              onKeyPress={handleKeyPress}
              className="w-full text-sm font-medium bg-transparent border-0 transition-colors duration-300 outline-none focus:outline-none focus:ring-0 focus:border-0 focus:shadow-none placeholder:text-gray-400 placeholder:font-normal focus:text-gray-900"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword((v) => !v)}
              className="text-gray-400 transition-colors duration-300 hover:text-emerald-600 focus:outline-none focus:text-emerald-700"
              aria-label="Toggle password visibility"
            >
              {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm Password Input */}
      <div className="relative group">
        <div className="relative rounded-xl p-[1px] bg-gradient-to-br from-emerald-400/30 via-transparent to-emerald-400/30 transition-all duration-300 group-hover:from-emerald-400/50 group-hover:to-emerald-400/50 group-focus-within:from-emerald-500/60 group-focus-within:to-emerald-500/60">
          <div className="flex items-center gap-3 rounded-[0.95rem] bg-white/95 px-4 py-3 ring-1 ring-gray-200/50 shadow-sm backdrop-blur-md transition-all duration-300 group-hover:ring-emerald-300/60 group-hover:shadow-md group-focus-within:ring-emerald-400/80 group-focus-within:shadow-lg group-focus-within:bg-white">
            <Lock
              size={18}
              className="text-emerald-600 transition-colors duration-300 group-focus-within:text-emerald-700"
            />
            <input
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm New Password"
              onKeyPress={handleKeyPress}
              className="w-full text-sm font-medium bg-transparent border-0 transition-colors duration-300 outline-none focus:outline-none focus:ring-0 focus:border-0 focus:shadow-none placeholder:text-gray-400 placeholder:font-normal focus:text-gray-900"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((v) => !v)}
              className="text-gray-400 transition-colors duration-300 hover:text-emerald-600 focus:outline-none focus:text-emerald-700"
              aria-label="Toggle password visibility"
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Password Requirements */}
      <div className="space-y-1 text-xs text-gray-500">
        <p>Password requirements:</p>
        <ul className="space-y-1 list-disc list-inside">
          <li>At least 8 characters long</li>
          <li>Contains uppercase and lowercase letters</li>
          <li>Contains at least one number</li>
        </ul>
      </div>

      {/* Reset Password Button */}
      <AuthButton
        onClick={handleResetPassword}
        loading={loading}
        loadingText="Resetting password..."
      >
        Reset Password
      </AuthButton>

      {/* Back to Login Link */}
      <Link
        to="/signin"
        className="flex gap-2 justify-center items-center text-xs text-emerald-600 hover:text-emerald-700"
      >
        <ArrowLeft size={14} />
        Back to login
      </Link>
    </AuthLayout>
  );
}
